/**
 * ExerciseSearchModal - Global search for exercises across all workout history
 * Shows results grouped by date with set details
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Loader2, Dumbbell, Calendar, ChevronRight, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useFitnessStore } from '../../stores/fitnessStore';
import { useCoachStore } from '../../stores/coachStore';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const ExerciseSearchModal = () => {
  const {
    isExerciseSearchModalOpen,
    exerciseSearchResults,
    exerciseSearchQuery,
    isSearchingExercises,
    closeExerciseSearchModal,
    searchExercises,
    openDayDetailModal,
  } = useFitnessStore();

  const { viewingClientId, viewingClientInfo } = useCoachStore();

  const [localQuery, setLocalQuery] = useState('');

  // Body scroll lock (iOS-safe)
  useBodyScrollLock(isExerciseSearchModalOpen);

  // Reset local query when modal opens
  useEffect(() => {
    if (isExerciseSearchModalOpen) {
      setLocalQuery(exerciseSearchQuery || '');
    }
  }, [isExerciseSearchModalOpen, exerciseSearchQuery]);

  // Debounced search - pass viewingClientId explicitly
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localQuery.trim().length >= 2) {
        console.log('[ExerciseSearchModal] Searching for:', localQuery, 'clientId:', viewingClientId);
        searchExercises(localQuery, viewingClientId);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localQuery, searchExercises, viewingClientId]);

  // ESC key to close
  useEffect(() => {
    if (!isExerciseSearchModalOpen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        closeExerciseSearchModal();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isExerciseSearchModalOpen, closeExerciseSearchModal]);

  // Group results by date
  const groupedResults = useMemo(() => {
    if (!exerciseSearchResults?.length) return {};

    const groups = {};
    exerciseSearchResults.forEach((workout) => {
      const date = workout.date;
      if (!groups[date]) {
        groups[date] = {
          date,
          exercises: {},
        };
      }

      const exerciseName = workout.exercise_name;
      if (!groups[date].exercises[exerciseName]) {
        groups[date].exercises[exerciseName] = [];
      }
      groups[date].exercises[exerciseName].push(workout);
    });

    return groups;
  }, [exerciseSearchResults]);

  // Get sorted dates (newest first)
  const sortedDates = useMemo(() => {
    return Object.keys(groupedResults).sort((a, b) => b.localeCompare(a));
  }, [groupedResults]);

  // Get unique exercise names count
  const uniqueExerciseCount = useMemo(() => {
    const names = new Set(exerciseSearchResults?.map(w => w.exercise_name) || []);
    return names.size;
  }, [exerciseSearchResults]);

  // Handle clicking on a date group to open DayDetailModal
  const handleDateClick = useCallback((date, workouts) => {
    closeExerciseSearchModal();
    openDayDetailModal(date, workouts);
  }, [closeExerciseSearchModal, openDayDetailModal]);

  if (!isExerciseSearchModalOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm pt-[10vh] px-4"
        onClick={closeExerciseSearchModal}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: -20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: -20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-lg bg-gray-900 rounded-2xl shadow-2xl border border-gray-800 max-h-[80vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-xl">
                <Search className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Egzersiz Ara</h2>
                {/* Show client info when coach is viewing a client */}
                {viewingClientId && viewingClientInfo && (
                  <div className="flex items-center gap-2 mt-0.5">
                    {viewingClientInfo.avatar_url ? (
                      <img
                        src={viewingClientInfo.avatar_url}
                        alt={viewingClientInfo.display_name || viewingClientInfo.email}
                        className="w-4 h-4 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-violet-500/30 flex items-center justify-center">
                        <User className="w-2.5 h-2.5 text-violet-400" />
                      </div>
                    )}
                    <span className="text-sm text-violet-400">
                      {viewingClientInfo.display_name || viewingClientInfo.email}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={closeExerciseSearchModal}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Input */}
          <div className="p-4 border-b border-gray-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={localQuery}
                onChange={(e) => setLocalQuery(e.target.value)}
                placeholder="Egzersiz adı ara... (min 2 karakter)"
                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                autoFocus
              />
              {isSearchingExercises && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-400 animate-spin" />
              )}
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {/* Results summary */}
            {exerciseSearchResults?.length > 0 && (
              <div className="px-4 py-2 bg-gray-800/50 text-sm text-gray-400 flex items-center gap-2">
                <Dumbbell className="w-4 h-4" />
                <span>
                  {uniqueExerciseCount} egzersiz, {exerciseSearchResults.length} kayıt bulundu
                </span>
              </div>
            )}

            {/* Empty state - no search yet */}
            {localQuery.length < 2 && (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <div className="p-4 bg-gray-800 rounded-full mb-4">
                  <Search className="w-8 h-8 text-gray-600" />
                </div>
                <p className="text-gray-400">Aramaya başlamak için en az 2 karakter girin</p>
              </div>
            )}

            {/* Empty state - no results */}
            {localQuery.length >= 2 && !isSearchingExercises && exerciseSearchResults?.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <div className="p-4 bg-gray-800 rounded-full mb-4">
                  <Dumbbell className="w-8 h-8 text-gray-600" />
                </div>
                <p className="text-gray-400">"{localQuery}" için sonuç bulunamadı</p>
                <p className="text-gray-500 text-sm mt-1">Farklı bir arama terimi deneyin</p>
              </div>
            )}

            {/* Results list */}
            {sortedDates.map((date) => {
              const group = groupedResults[date];
              const allWorkoutsForDate = Object.values(group.exercises).flat();

              return (
                <div key={date} className="border-b border-gray-800 last:border-b-0">
                  {/* Date header */}
                  <button
                    onClick={() => handleDateClick(date, allWorkoutsForDate)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-800/50 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span className="text-white font-medium">
                        {format(parseISO(date), 'd MMMM yyyy, EEEE', { locale: tr })}
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-orange-400 transition-colors" />
                  </button>

                  {/* Exercises for this date */}
                  <div className="px-4 pb-3">
                    {Object.entries(group.exercises).map(([exerciseName, sets]) => (
                      <div key={exerciseName} className="ml-7 mb-2 last:mb-0">
                        <div className="text-sm font-medium text-orange-400 mb-1">
                          {exerciseName}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {sets.map((set, idx) => (
                            <div
                              key={set.id || idx}
                              className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-300"
                            >
                              {set.reps != null && set.weight != null ? (
                                <>
                                  <span className="text-white">{set.reps}</span>
                                  <span className="text-gray-500"> tekrar </span>
                                  <span className="text-orange-400">{set.weight}</span>
                                  <span className="text-gray-500"> {set.weight_unit || 'kg'}</span>
                                </>
                              ) : set.reps != null ? (
                                <>
                                  <span className="text-white">{set.reps}</span>
                                  <span className="text-gray-500"> tekrar</span>
                                </>
                              ) : set.weight != null ? (
                                <>
                                  <span className="text-orange-400">{set.weight}</span>
                                  <span className="text-gray-500"> {set.weight_unit || 'kg'}</span>
                                </>
                              ) : (
                                <span className="text-gray-500">Set {idx + 1}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default ExerciseSearchModal;
